'use client';
import { forwardRef } from 'react';
import { template2PlaceholderData, TemplateProps } from '@zeus/shared';
import useFont from '../../../hooks/useFont';
import { getScaleSize } from '../../../utils/utils';
import Menu from './components/Menu';
import { useNormalizedPixels } from '../../../hooks/useResize';
import Media from '../Media';
import BrandLogo from './components/BrandLogo';
import { BoxComponent } from '../../../base/box/Box';
import { TypographyComponent } from '../../../base/typography/Typography';

export const Template2 = forwardRef<HTMLDivElement, TemplateProps>((props, ref) => {
  const { template, scale = 1, style, development, timezone, templateRef, active, shouldLoad = true } = props;

  useFont(template?.fontFamily);

  const content = Array.isArray(template?.content) ? template?.content : [];
  const menus = content.length > 0 ? content : template2PlaceholderData;
  const { content: templateContent, ...rest } = template;

  const { normalizePixels } = useNormalizedPixels();

  const bgStyles = () => {
    if (template?.background?.isEnabled && template?.background?.image) {
      return { backgroundImage: `url(${template?.background?.image})` };
    }

    if (template?.background?.color) {
      return { backgroundColor: template?.background?.color };
    }

    return {
      backgroundImage: `url('https://dashboard-demo-assests.s3.us-west-2.amazonaws.com/dashboard/menuboard/Template2bg.webp')`,
    };
  };

  const isBrandLogo = template?.brandLogo?.isEnabled && template?.brandLogo?.image;
  const media = template?.media?.mediaContent;
  const isMedia = template.media?.isEnabled && media?.url;
  const isLayoutOne = isMedia && template.layoutType === 1;
  const isLayoutTwo = isMedia && template.layoutType === 2;

  return (
    <div
      ref={templateRef}
      style={{
        backgroundSize: 'cover',
        overflow: 'hidden',
        display: 'flex',
        position: 'relative',
        justifyContent: 'space-between',
        padding: isLayoutOne ? '0px' : getScaleSize(normalizePixels(50, 'height'), scale),
        ...bgStyles(),
        ...(scale ? { transformOrigin: 'top left' } : {}),
        ...(!style ? { height: '100vh', width: '100vw' } : style),
      }}
    >
      <div
        ref={ref}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: 'column',
          width: isLayoutOne ? '75%' : '100%',
          height: '100%',
          alignContent: isLayoutOne ? 'space-between' : 'flex-start',
          overflow: 'hidden',
          rowGap: '2%',
          columnGap: '1%',
          position: 'relative',
          padding: isLayoutOne
            ? `
          ${getScaleSize(normalizePixels(50, 'height'), scale)}
          ${getScaleSize(normalizePixels(20, 'width'), scale)}
          ${getScaleSize(normalizePixels(50, 'height'), scale)}
          ${getScaleSize(normalizePixels(50, 'width'), scale)}
          `
            : '0px',
        }}
      >
        {isBrandLogo && <BrandLogo src={template.brandLogo?.image as string} scale={scale} />}

        {Array.isArray(menus) &&
          menus.length > 0 &&
          menus.map((menu, menuIndex) => (
            <Menu
              key={menu.id}
              menu={menu}
              template={rest}
              development={development}
              menuIndex={menuIndex}
              timezone={timezone}
              scale={scale}
            />
          ))}

        {isLayoutTwo && (
          <div
            id="temp2-layout-2-media"
            style={{
              width: '24%',
              position: 'absolute',
              bottom: '0',
              right: '1%',
              height: getScaleSize(normalizePixels(500, 'height'), scale),
            }}
          >
            <Media
              isDevelopment={development}
              media={media}
              borderRadius="0px"
              playing={active}
              shouldLoad={shouldLoad}
            />
          </div>
        )}
      </div>

      {isLayoutOne && (
        <div style={{ width: '25%', position: 'absolute', right: '0', height: '100%' }}>
          <Media
            isDevelopment={development}
            media={media}
            borderRadius="0px"
            height={'100%'}
            playing={active}
            shouldLoad={shouldLoad}
          />
        </div>
      )}

      {template.taxLabel.isEnabled && (
        <BoxComponent
          sx={{ position: 'absolute', left: getScaleSize(normalizePixels(68, 'width'), scale), bottom: '1%' }}
        >
          <TypographyComponent sx={{ fontWeight: '500', fontSize: normalizePixels(12, 'height') }} variant="subtitle1">
            {template.taxLabel.taxText}
          </TypographyComponent>
        </BoxComponent>
      )}
    </div>
  );
});
